import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import VideoArticlesGrid from '../../../components/VideoArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import {
  getVideoArticles,
  VideoArticle,
} from '../../../server/libs/videoArticles';
import withAuth from '../../../server/libs/withAuth';
import { Tag } from '../../tagsearch/[tagIds]';
import {
  findTagsByNames,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';
import { tagsString } from '../../../common/libs/tag';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import useSpecialPage from '../../../hooks/useSpecialPage';

export const setting = {
  specialPageSlug: SPECIALS.homeMedicalCare.name,
  specialPageShortName: 'home_med',
  tagTexts: SPECIALS.homeMedicalCare.tags,
  specialPagePath: `/special/${SPECIALS.homeMedicalCare.name}`,
  specialPageTitle: SPECIALS.homeMedicalCare.alt,
  sideBanner: ({
    isDemoAccount,
    isCorporationAccount,
  }: {
    isDemoAccount: boolean;
    isCorporationAccount: boolean;
  }) => (
    <>
      <Box _hover={{ opacity: '0.8' }}>
        <Link
          isExternal
          href="https://forms.gle/FmbUziMV6AbTL6Cq9"
          onClick={() =>
            sendAnalyticsEvent(
              'special_contents',
              'click',
              `${setting.specialPageShortName}_career`,
            )
          }
        >
          <Image
            src="/images/special/home_med_career_banner_720*618.png"
            alt="ロールモデルを知ることから始めよう キャリア相談 申込受付中"
            width={720}
            height="auto"
          />
        </Link>
      </Box>
      {!isDemoAccount && !isCorporationAccount && (
        <Box>
          <Link
            isExternal
            href="https://hakuseikai-partners.com/"
            onClick={() =>
              sendAnalyticsEvent(
                'special_contents',
                'click',
                `${setting.specialPageShortName}_hakuseikai`,
              )
            }
          >
            <Image
              src="/images/special/home_med_hakuseikai_banner_720*202.png"
              alt="医療法人 白青会 パートナーシップ制度"
              width={720}
              height="auto"
            />
          </Link>
        </Box>
      )}
    </>
  ),
};

const mainImagePath = '/images/special/home-medical-care_main_1300*720.png';
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内

const description = (
  <>
    <Text fontSize="sm" fontWeight="bold" mb={2}>
      企画概要
    </Text>
    <Text fontSize="sm">
      2040年問題に向けて在宅医療ニーズが高まる中、全国各地の先生方が様々な背景や思いで奮闘している『在宅医療』
      <br />
      医師が伝える事例をもとに、在宅医療の現場の知見や最新知識、そして在宅医療へのキャリアパスなど、在宅医療のリアルを知ること、学ぶこと、伝えることができる様々なスライドを公開中です！
      <br />
      <br />
      在宅医療に従事されている先生方へ
      <br />
      日頃の勉強会などで活用されたスライドの投稿をお待ちしております
    </Text>
    <Flex justifyContent="flex-end">
      <NextLink href={`/article/upload?tags=${setting.tagTexts[0]}`} passHref>
        <Button
          size="sm"
          as="a"
          color="white"
          bg="brand.500"
          onClick={() => {
            sendAnalyticsEvent(
              'special_contents',
              'click',
              `${setting.specialPageShortName}Upload`,
            );
          }}
          textAlign="center"
          display="inline-block"
          p={2}
          pl={{ base: 2, md: 4 }}
          pr={{ base: 2, md: 4 }}
          ml={4}
          height="auto"
          w={{ base: '50%', md: '30%' }}
          _hover={{
            opacity: '0.8',
          }}
        >
          <Text as="span">#{setting.tagTexts[0]}</Text>
          <br />
          <Text as="span" fontSize="xs">
            でスライド投稿
          </Text>
        </Button>
      </NextLink>
    </Flex>
  </>
);

// ここより下は特集ページごとの固有の内容は無し

type PageContext = {
  tags: Tag[];
  articles: ArticleHeader[];
  videoArticles: VideoArticle[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

const HomeMedicalCarePage: NextPage<PageContext> = ({
  tags,
  articles,
  videoArticles,
  isMobile,
  isDemoAccount,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isPrivate,
}) => {
  const {
    specialPageSlug,
    specialPageTitle,
    specialPagePath,
    specialPageShortName,
    sideBanner,
  } = setting;

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt={specialPageTitle}
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>{description}</Box>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {articles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {tagsString(tags)} に関するスライド
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={articles}
                      isMobile={isMobile}
                      gaEventLabel={`${specialPageShortName}_tagsearch`}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {videoArticles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {tagsString(tags.slice(0, 1))} に関する動画
                    </Text>
                  </Flex>
                  <Flex direction={{ base: 'column', md: 'row' }}>
                    <VideoArticlesGrid
                      videoArticles={videoArticles}
                      isMobile={isMobile}
                      gaEventLabel={`${specialPageShortName}_video`}
                      articleId={specialPageSlug}
                      maxColumns={2}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {sideBanner({ isDemoAccount, isCorporationAccount })}
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default HomeMedicalCarePage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { specialPageSlug, tagTexts } = setting;

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let articles: ArticleHeader[] = [];
    const targetTags = await findTagsByNames(tagTexts);
    const tags: Tag[] = targetTags.map((tag) => ({
      id: tag.tag_id,
      name: tag.tag_name,
    }));
    if (tags.length) {
      const articleIdList: string[] = await getArticleIdsWithTagIds(
        tags.map((tag) => tag.id),
      );

      const articleHeaders = await prisma.article_header.findMany({
        select: {
          article_id: true,
          name: true,
          article_type: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIdList,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
        orderBy: {
          create_date: 'desc',
        },
      });
      // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
      const discloseIds = articleHeaders
        .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
        .filter((v) => !!v);
      // アカウント統合済みのユーザのプロフィールのみ返却される
      const idBaseProfileMap = await fetchAccountIntegrationProfiles(
        discloseIds,
      );

      articles = articleHeaders.reduce((articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: article.user_article_header_author_idTouser.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds:
              article.user_article_header_author_idTouser.update_date.getTime(),
            profilePhotoS3Key:
              article.user_article_header_author_idTouser.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      }, []);
    }

    const videoArticles = await getVideoArticles(specialPageSlug);

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        tags,
        articles,
        isMobile,
        isDemoAccount,
        videoArticles,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
